import Axios from 'axios';
import { action, observable } from 'mobx';
import { PageInfo, PlanParams, ProfitParams, UpdatePlanParams } from '@/models/data';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import { TriggerPriceType } from '@/pages/futures/main/orderForm/formSection';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { TpslConditionType } from '@/pages/futures/main/orderForm/tpslCondition';
import {
  getBizErrorCode,
  triggerWelfareOppositeOpenModal,
  WELFARE_OPPOSITE_OPEN_CODE,
} from '@/components/welfareOppositeOpenModal/bridge';

export interface PlanItem {
  [x: string]: string;
  closePosition: boolean; // 是否触发全平
  createdTime: number; // 创建时间
  entrustId: string; // 委托id
  entrustType: string; // 委托类型
  isOrdinary: boolean; // 是否是普通计划单
  marketOrderLevel: number; // 市价最优档
  orderSide: OrderSide; // 买卖方向
  origQty: string; // 数量
  positionSide: PositionSide; // 持仓方向
  price: string; // 订单价格
  state: string; // 订单状态 NOT_TRIGGERED：新建委托（未触发）；TRIGGERING：触发中；TRIGGERED：已触发；USER_REVOCATION：已撤销；PLATFORM_REVOCATION：平台撤销（拒绝）；EXPIRED：已过期	string
  stopPrice: string; // 触发价格
  symbol: string; // 交易对
  timeInForce: string; // 有效方式
  triggerPriceType: string; // 触发价格类型
  profitTriggerPriceType?: TriggerPriceType;
  triggerProfitPrice?: string;
  triggerProfitConditionType?: TpslConditionType;
  triggerProfitConditionValue?: string;
  triggerStopPrice?: string;
  triggerStopConditionType?: TpslConditionType;
  triggerStopConditionValue?: string;
  updatedTime: number;
  /** 计划单触发失败原因码，如 WELFARE_OPPOSITE_OPEN_CONFLICT */
  failReasonCode?: string | null;

  // 其他配置项
  contractSize: string;
  marginFrozen?: string; // 占用保证金
  leverage?: number;
  positionType?: PositionType;
  positionModel?: PositionMode;
}

export interface ProfitItem {
  positionType: PositionType | undefined;
  positionModel: PositionMode | undefined;
  leverage: number;
  currentPrice: ReactNode;
  activatePrice: string;
  triggerPrice: string;
  /** 1：止盈，2：止损 */
  triggerPriceSide?: 1 | 2 | string | number;
  orderId?: string;
  positionId: string;
  createdTime: number;
  entryPrice: string;
  origQty: string;
  /** 历史止盈止损实际成交的平仓数量（张） */
  executedQty?: string;
  positionSize?: string;
  positionSide: PositionSide;
  profitId: string;
  state: string;
  symbol: string;
  isUBased: boolean;
  unrealizedPnl: string;
  distanceRate: string;
  distancePrice?: string;
  triggerProfitPrice: string;
  triggerProfitConditionType?: TpslConditionType;
  triggerProfitConditionValue?: string;
  triggerStopPrice: string;
  triggerStopConditionType?: TpslConditionType;
  triggerStopConditionValue?: string;
  triggerPriceType: TriggerPriceType;
  profitType?: 'ALL' | 'NORMAL';
  failReasonCode?: string;
  failReason?: string;
  /** 历史止盈止损的实际执行价格 */
  executionPrice?: string;
  /** 历史止盈止损的实际触发时间 */
  triggerTime?: number;
  updatedTime?: number;

  // 其他配置信息
  contractSize: string;
  liquidationPrice: string;
  pricePrecision: number;
}

export interface moveProfitItem {
  createdTime: number;
  entryPrice: string;
  origQty: string;
  positionSide: PositionSide;
  moveProfitId: string;
  state: string;
  symbol: string;
  moveProfitPrice: string;
  moveProfitDistance: string;
  activatePrice?: string;
  distanceRate?: string;
  distancePrice?: string;
  triggerPriceType: TriggerPriceType;
  unrealizedPnl: string;

  // 其他配置信息
  contractSize: string;
  liquidationPrice: string;
  pricePrecision: number;
  markPrice?: string;
}

export default class Order {
  @observable planList: PlanItem[];

  @observable planListPageInfo: PageInfo;

  @observable withdrawPlanList: string[];

  @observable profitList: ProfitItem[];

  @observable profitListPageInfo: PageInfo;

  @observable withdrawProfitList: string[];

  @observable moveProfitList: moveProfitItem[];

  @observable moveProfitListPageInfo: PageInfo;

  @observable withdrawMoveProfitList: string[];

  @observable isFetchingPlanList: boolean;

  constructor() {
    this.planList = [];
    this.planListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };
    this.withdrawPlanList = [];

    this.profitList = [];
    this.profitListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.withdrawProfitList = [];

    this.moveProfitList = [];
    this.moveProfitListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };
    this.withdrawMoveProfitList = [];

    this.isFetchingPlanList = false;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  //
  // 计划委托
  //

  @action
  setPlanList(data: PlanItem[]) {
    this.planList = data;
  }

  @action.bound
  setPlanListPageInfo(data: PageInfo) {
    const origin = { ...this.planListPageInfo };
    this.planListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchPlanList(params?: {
    symbol?: string;
    page?: number;
    size?: number;
    state?: string;
    underlyingType?: UnderLyingEnum;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingPlanList', true);
    const planListPageInfo = this.planListPageInfo;

    const config: any = {
      params: {
        page: planListPageInfo.page,
        size: planListPageInfo.size,
        ...params,
      },
    };

    if (params?.symbol) {
      config.baseURL = getBaseUrlBySymbol(params.symbol);
    } else if (params?.underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(params?.underlyingType);
    }

    Reflect.deleteProperty(config.params, 'underlyingType');

    const result: any = await Axios.get(API.ENTRUST_PLAN_LIST, config);

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setPlanList(items);

        this.setPlanListPageInfo({ page, size, total });
      }
    }

    this.setLoadingState('isFetchingPlanList', false);
  }

  @action.bound
  async fetchCancelPlan(id: string) {
    this.setWithdrawPlanList([...this.withdrawPlanList, id]);
    const result: any = await Axios.post(API.ENTRUST_PLAN_CANCEL, { entrustId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });


    if (result) {
      const newList = [...this.planList];
      const index = newList.findIndex((item: PlanItem) => id === item.entrustId);
      newList.splice(index, 1);
      this.setPlanList(newList);

      const { page, size, total } = this.planListPageInfo;
      this.setPlanListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
    }


    this.setWithdrawPlanList([...this.withdrawPlanList, id]);
  }

  // 撤销所有计划委托
  @action.bound
  async fetchCancelAllPlan(symbol?: string, positionSource: 'ALL' | 'SELF' | 'EXPERIENCE' = 'ALL') {
    // this.setWithdrawPlanList([...this.withdrawPlanList, symbol]);
    const result: any = await Axios.post(API.ENTRUST_PLAN_CANCEL_ALL, { symbol, positionSource }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });


    if (result) {
      const data = {
        page: 1,
        state: 'UNFINISHED',
        symbol,
      };
      this.fetchPlanList(data);
      // const newList = [...this.planList];
      // const index = newList.findIndex((item: PlanItem) => symbol === item.symbol);
      // newList.splice(index, 1);
      // this.setPlanList(newList);

      // const { page, size, total } = this.planListPageInfo;
      // this.setPlanListPageInfo({ page, size, total: total - 1 });
      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
      return Promise.resolve(result);

    }

    // this.setWithdrawPlanList([...this.withdrawPlanList, symbol]);
  }
  // 更新现有计划委托列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addPlanToList(newItem: PlanItem, isLocal = false) {
  //   const { page, size, total } = this.planListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [newItem, ...this.planList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setPlanList(newList);
  //   this.setPlanListPageInfo({ page, size, total: total + 1 });
  // }

  @action.bound
  setWithdrawPlanList(data: string[]) {
    this.withdrawPlanList = data;
  }

  @action.bound
  isWithdrawingPlan(id: string) {
    return this.withdrawPlanList.includes(id);
  }

  @action.bound
  async fetchCreatePlan(params: PlanParams) {
    try {
      const result: any = await Axios.post(API.ENTRUST_PLAN_CREATE, params);
      if (getBizErrorCode(result) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
        return { ok: false as const, code: WELFARE_OPPOSITE_OPEN_CODE };
      }
      if (result) {
        Notification.success({
          message: i18n.t('success.order'),
          offset: 60,
          duration: 2000,
        });
        void this.fetchPlanList({
          symbol: localStorage.getItem('isShowCurrentSymbolOnly') === 'true' ? params.symbol : undefined,
          page: 1,
          state: 'UNFINISHED',
        }).catch(() => undefined);
      }
      return { ok: true as const, result };
    } catch (err: any) {
      if (getBizErrorCode(err) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
        return { ok: false as const, code: WELFARE_OPPOSITE_OPEN_CODE };
      }
      Notification.error({
        message: err?.error?.msg || err?.error || err?.msgInfo || err?.msg || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
      return { ok: false as const };
    }
  }

  @action.bound
  async fetchPlanDetail(entrustId: string, symbol: string) {
    const result: any = await Axios.get(API.ENTRUST_PLAN_DETAIL, {
      params: { entrustId },
      baseURL: getBaseUrlBySymbol(symbol),
    }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
    });

    return result || null;
  }

  @action.bound
  async fetchUpdatePlan(params: UpdatePlanParams, symbol: string) {
    const result: any = await Axios.post(API.ENTRUST_PLAN_UPDATE, params, {
      baseURL: getBaseUrlBySymbol(symbol),
    }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      Notification.success({
        message: i18n.t('success.order'),
        offset: 60,
        duration: 2000,
      });
      return result;
    }

    return null;
  }


  //
  // 止盈止损
  //
  @action
  setProfitList(data: ProfitItem[]) {
    this.profitList = data;
  }

  @action.bound
  setProfitListPageInfo(data: PageInfo) {
    const origin = { ...this.profitListPageInfo };
    this.profitListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchProfitList(
    params?: {
      symbol?: string;
      positionId?: string;
      page?: number;
      size?: number;
      state?: string;
      startTime?: number;
      endTime?: number;
      /** Step28：ALL / POSITION_EXPERIENCE；不传保持原 NORMAL */
      source?: string;
      cursor?: string;
    },
    retried = false,
  ) {
    const profitListPageInfo = this.profitListPageInfo;
    const isHistory = String(params?.state || '').toUpperCase() === 'HISTORY';
    // Step28：历史止盈止损走 getProfitHistory；当前走 getProfitEntrusts
    const url = isHistory ? API.ENTRUST_PROFIT_HISTORY : API.ENTRUST_PROFIT_LIST;
    const requestParams: Record<string, unknown> = {
      page: params?.page ?? profitListPageInfo.page,
      size: params?.size ?? profitListPageInfo.size,
      ...params,
    };
    // NORMAL：不传 source，保持原分页
    if (!params?.source || params.source === 'NORMAL') {
      delete requestParams.source;
      delete requestParams.cursor;
    } else if (!params?.cursor) {
      delete requestParams.cursor;
    } else {
      // 聚合翻页：透传 nextCursor，勿再带 page
      delete requestParams.page;
    }

    try {
      const result: any = await Axios.get(url, {
        params: requestParams,
      });

      if (result) {
        // 聚合分页：hasNext + nextCursor；页码型 total=-1 时仍用 items
        const items = Array.isArray(result.items) ? result.items : Array.isArray(result) ? result : [];
        const page = result.page ?? requestParams.page ?? profitListPageInfo.page;
        const size = result.ps ?? result.size ?? requestParams.size ?? profitListPageInfo.size;
        const pageNum = Number(page) || 1;
        const sizeNum = Number(size) || 10;
        const hasNext = Boolean(result.hasNext);
        // total=-1：仅看 hasNext；构造可点「下一页」的假 total
        const total =
          result.total === -1
            ? hasNext
              ? pageNum * sizeNum + 1
              : (pageNum - 1) * sizeNum + items.length
            : result.total ?? items.length;
        this.setProfitList(
          items.map((item: any) => ({
            ...item,
            source: item.source || (item.isExperiencePosition ? 'POSITION_EXPERIENCE' : 'NORMAL'),
          })),
        );
        this.setProfitListPageInfo({ page: pageNum, size: sizeNum, total: Number(total) });
        (this as any).profitListNextCursor = result.nextCursor || null;
        (this as any).profitListHasNext = hasNext;
      }
    } catch {
      if (params?.source && !retried) {
        const { source: _source, cursor: _cursor, ...rest } = params;
        await this.fetchProfitList(rest, true);
      }
    }
  }

  @action.bound
  async fetchWithdrawProfit(id: string) {
    this.setWithdrawProfitList([...this.withdrawProfitList, id]);
    const result: any = await Axios.post(API.ENTRUST_PROFIT_CANCEL, { profitId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });


    if (result) {
      window.dispatchEvent(new Event('kline-protection-orders-changed'));
      const newList = [...this.profitList];
      const index = newList.findIndex((item: ProfitItem) => id === item.profitId);
      newList.splice(index, 1);
      this.setProfitList(newList);

      const { page, size, total } = this.profitListPageInfo;
      this.setProfitListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
    }


    this.setWithdrawProfitList([...this.withdrawProfitList, id]);
  }

  // 撤销所有止盈止损
  @action.bound
  async fetchAllWithdrawProfit(symbol?: string, positionSource: 'ALL' | 'SELF' | 'EXPERIENCE' = 'ALL') {
    const result: any = await Axios.post(API.ENTRUST_PROFIT_ALL_CANCEL, { symbol, positionSource }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
      });
    });

    if (result) {
      window.dispatchEvent(new Event('kline-protection-orders-changed'));
      const data: any = {
        page: 1,
        state: 'UNFINISHED',
        symbol,
      };
      if (positionSource === 'EXPERIENCE') {
        data.source = 'POSITION_EXPERIENCE';
      } else if (positionSource === 'ALL') {
        data.source = 'ALL';
      }
      this.fetchProfitList(data);

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
      });
      return Promise.resolve(result);
    }
  }

  @action.bound
  setWithdrawProfitList(data: string[]) {
    this.withdrawProfitList = data;
  }

  @action.bound
  isWithdrawingProfit(id: string) {
    return this.withdrawProfitList.includes(id);
  }

  // 更新现有止盈止损列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addProfitToList(newItem: ProfitItem, isLocal = false) {
  //   const { page, size } = this.profitListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [{ ...newItem }, ...this.profitList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setProfitList(newList);
  // }

  // 修改止盈止损
  @action.bound
  async fetchUpdateProfit(params: ProfitParams) {
    const { profitId } = params;

    try {
      const result: any = await Axios.post(API.ENTRUST_PROFIT_UPDATE, params).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.updateStopProfit'),
          offset: 60,
          duration: 2000,
        });
      });


      if (result) {
        window.dispatchEvent(new Event('kline-protection-orders-changed'));
        const newList = [...this.profitList];
        const index = newList.findIndex((item: ProfitItem) => item.profitId === profitId);

        if (index > -1) {
          const item = newList[index];
          // @ts-ignore
          newList[index] = { ...item, ...params };
          this.setProfitList(newList);
        }

        Notification.success({
          message: i18n.t('success.updateStopProfit'),
          offset: 60,
          duration: 2000,
        });
      }


      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //
  // 移动止盈止损
  //

  @action
  setMoveProfitList(data: moveProfitItem[]) {
    this.moveProfitList = data;
  }

  @action.bound
  setMoveProfitListPageInfo(data: PageInfo) {
    this.moveProfitListPageInfo = data;
  }

  @action.bound
  setwithdrawMoveProfitList(data: string[]) {
    this.withdrawMoveProfitList = data;
  }

  // 获取移动止盈止损列表
  @action.bound
  async fetchmoveProfitList(
    params: {
      symbol?: string;
      page?: number;
      size?: number;
      state?: string;
      startTime?: number;
      endTime?: number;
      /** Step28：ALL / POSITION_EXPERIENCE；不传保持原 NORMAL */
      source?: string;
      cursor?: string;
    },
    retried = false,
  ) {
    this.setLoadingState('isFetchingmoveProfitList', true);

    const isHistory = String(params?.state || '').toUpperCase() === 'HISTORY';
    // Step28：历史走 getMoveProfitHistory；当前走 getMoveProfitList
    const url = isHistory ? API.TRAILING_STOP_HISTORY : API.TRAILING_STOP_LIST;
    const requestParams: Record<string, unknown> = {
      page: 1,
      size: 10,
      ...params,
    };
    // NORMAL：不传 source，保持原分页
    if (!params?.source || params.source === 'NORMAL') {
      delete requestParams.source;
      delete requestParams.cursor;
    } else if (!params?.cursor) {
      delete requestParams.cursor;
    } else {
      // 聚合翻页：透传 nextCursor，勿再带 page
      delete requestParams.page;
    }

    try {
      const result: any = await Axios.get(url, { params: requestParams });

      if (result) {
        const items = Array.isArray(result.items) ? result.items : Array.isArray(result) ? result : [];
        const pageNum = Number(result.page ?? requestParams.page ?? 1) || 1;
        const sizeNum = Number(result.ps ?? result.size ?? requestParams.size ?? 10) || 10;
        const hasNext = Boolean(result.hasNext);
        // 聚合响应 total=-1，是否还有下一页只看 hasNext
        const total =
          result.total === -1
            ? hasNext
              ? pageNum * sizeNum + 1
              : (pageNum - 1) * sizeNum + items.length
            : result.total ?? items.length;

        this.setMoveProfitList(
          items.map((item: any) => ({
            ...item,
            source: item.source || (item.isExperiencePosition ? 'POSITION_EXPERIENCE' : 'NORMAL'),
            canOperate: item.canOperate !== false,
          })),
        );
        this.setMoveProfitListPageInfo({ page: pageNum, size: sizeNum, total: Number(total) });
        (this as any).moveProfitListNextCursor = result.nextCursor || null;
        (this as any).moveProfitListHasNext = hasNext;
      }
    } catch {
      // 后端未支持 source 时回退到原查询，避免列表整体空白
      if (params?.source && !retried) {
        const { source: _source, cursor: _cursor, ...rest } = params;
        await this.fetchmoveProfitList(rest, true);
      }
    } finally {
      this.setLoadingState('isFetchingmoveProfitList', false);
    }
  }

  // 撤销移动止盈止损
  @action.bound
  async fetchWithdrawMoveProfit(moveProfitId: string) {
    this.setwithdrawMoveProfitList([...this.withdrawMoveProfitList, moveProfitId]);

    try {
      const result: any = await Axios.post(API.TRAILING_STOP_CANCEL, {
        profitId: moveProfitId,
      }).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.withdraw'),
          offset: 60,
          duration: 2000,
        });
      });

      if (result) {
        window.dispatchEvent(new Event('kline-protection-orders-changed'));
        Notification.success({
          message: i18n.t('success.withdraw'),
          offset: 60,
          duration: 2000,
        });

        // 从列表中移除
        const newList = this.moveProfitList.filter(item => item.moveProfitId !== moveProfitId);
        this.setMoveProfitList(newList);

        return result;
      }
    } catch (e) {
      // do nothing
    } finally {
      this.setwithdrawMoveProfitList(this.withdrawMoveProfitList.filter(id => id !== moveProfitId));
    }
  }

  // 撤销所有移动止盈止损
  @action.bound
  async fetchAllWithdrawMoveProfit(symbol?: string, positionSource: 'ALL' | 'SELF' | 'EXPERIENCE' = 'ALL') {
    try {
      const result: any = await Axios.post(API.TRAILING_STOP_CANCEL_ALL, {
        symbol,
        positionSource,
      }).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.withdraw'),
          offset: 60,
          duration: 2000,
        });
      });

      if (result) {
        window.dispatchEvent(new Event('kline-protection-orders-changed'));
        Notification.success({
          message: i18n.t('success.withdraw'),
          offset: 60,
          duration: 2000,
        });

        return result;
      }
    } catch (e) {
      // do nothing
    }
  }

  // 创建移动止盈止损
  @action.bound
  async fetchCreateMoveProfit(params: any) {
    try {
      const result: any = await Axios.post(API.TRAILING_STOP_CREATE, params).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.createmoveProfit'),
          offset: 60,
          duration: 2000,
        });
      });

      if (result) {
        window.dispatchEvent(new Event('kline-protection-orders-changed'));
        Notification.success({
          message: i18n.t('success.createmoveProfit'),
          offset: 60,
          duration: 2000,
        });

        return result;
      }
    } catch (e) {
      // do nothing
    }
  }

  // 修改移动止盈止损
  @action.bound
  async fetchUpdateMoveProfit(params: any) {
    try {
      const result: any = await Axios.post(API.TRAILING_STOP_UPDATE, params).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.updatemoveProfit'),
          offset: 60,
          duration: 2000,
        });
      });

      if (result) {
        window.dispatchEvent(new Event('kline-protection-orders-changed'));
        Notification.success({
          message: i18n.t('success.updatemoveProfit'),
          offset: 60,
          duration: 2000,
        });

        return result;
      }
    } catch (e) {
      // do nothing
    }
  }
}
